/**
 * StageWizard — V14.90 : parcours guidé 4 étapes pour un lot, depuis la galerie.
 * 1 Blanche → 2 Texturer → 3 Meubler → 4 Lumière. À chaque étape : lancer,
 * choisir un rendu, « Valider & ancrer » (le backend ancre automatiquement).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Anchor, CheckCircle2, Loader2, Play, Share2, Wand2, ChevronRight } from 'lucide-react';
import RenderReviewPanel from './RenderReviewPanel';
import StageScenarioCard from './StageScenarioCard';
import { emitRefsChanged, useLauncherParams, patchLauncherParams, RESET_PARAM } from '../../lib/generationEvents';
import DeterministicWhiteboxButton from './DeterministicWhiteboxButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };
const fullImg = (path) => `${process.env.REACT_APP_BACKEND_URL}${path}`;

export const WIZARD_STEPS = [
  { id: 'whitebox', n: 1, title: 'Maquette blanche', desc: 'Extrusion mathématique du tracé (sans IA, 0 crédit) — géométrie exacte, caméra iso 45°.', refFlag: 'is_whitebox_reference', refKey: 'whitebox_reference_job_id' },
  { id: 'textured', n: 2, title: 'Texturer', desc: 'Matériaux de la Matériauthèque sur la maquette validée.', refFlag: 'is_textured_reference', refKey: 'textured_reference_job_id' },
  { id: 'furnished', n: 3, title: 'Meubler', desc: 'Mobilier du catalogue, pièce par pièce.', refFlag: 'is_furnished_reference', refKey: 'furnished_reference_job_id' },
  { id: 'final', n: 4, title: 'Lumière & post-prod', desc: 'Heure du jour, ombres, ambiance, image finale.', refFlag: null, refKey: null },
];

const StepHeader = ({ step, state, active, onClick }) => (
  <button type="button" onClick={onClick} data-testid={`wizard-step-${step.id}`}
          className="flex items-center gap-2 text-left rounded-lg px-2.5 py-1.5 transition-colors"
          style={{ background: active ? 'var(--c-surface-3)' : 'transparent', border: `1px solid ${active ? 'var(--c-border-accent)' : 'transparent'}` }}>
    <span className="w-6 h-6 rounded-full inline-flex items-center justify-center text-[11px] font-bold shrink-0"
          style={state === 'done' ? { background: 'rgba(34,197,94,0.2)', color: '#4ADE80' } : state === 'ready' ? { background: 'rgba(217,160,91,0.2)', color: '#F5C77E' } : { background: 'rgba(255,255,255,0.06)', color: 'var(--c-text-faint)' }}>
      {state === 'done' ? <CheckCircle2 className="w-3.5 h-3.5" /> : step.n}
    </span>
    <span className="min-w-0">
      <span className="block text-[11.5px] font-semibold truncate" style={{ color: state === 'locked' ? 'var(--c-text-faint)' : 'var(--c-text)' }}>{step.title}</span>
      <span className="block text-[9.5px] uppercase tracking-wider" style={{ color: 'var(--c-text-faint)' }}>
        {state === 'done' ? 'validée' : state === 'ready' ? 'à faire' : state === 'pending' ? 'en cours…' : 'verrouillée'}
      </span>
    </span>
  </button>
);

const StageWizard = ({ open, onClose, projectId, items, lots, initialLotId, onChanged, onOpenLauncher }) => {
  const lotOptions = useMemo(() => (lots || []).map((l) => ({ id: l.lot_id, label: l.lot_number || l.name || l.lot_id })), [lots]);
  const bestLot = useMemo(() => {
    const score = {};
    (items || []).forEach((i) => { if (i.params?.render_stage) score[i.lot_id] = (score[i.lot_id] || 0) + 1; });
    return Object.entries(score).sort((a, b) => b[1] - a[1])[0]?.[0];
  }, [items]);
  const [lotId, setLotId] = useState(initialLotId || bestLot || lotOptions[0]?.id || '');
  const [stepId, setStepId] = useState('whitebox');
  const [busy, setBusy] = useState(false);
  const [choice, setChoice] = useState({});
  // V14.93 — état optimiste : étapes validées dans cette session (le parent
  // recharge la galerie en arrière-plan ; on n'attend pas pour débloquer).
  const [justDone, setJustDone] = useState({});
  const [scenario, setScenario] = useState(null);
  const [scenarioLoading, setScenarioLoading] = useState(false);
  // V14.94 — paramètres : config projet + ajustements du lanceur + édition locale.
  const [cfgValues, setCfgValues] = useState(null);
  const [overrides, setOverrides] = useState({});
  const launcherParams = useLauncherParams();
  useEffect(() => {
    axios.get(`${API}/projects/${projectId}/prompt-config`, opts)
      .then((r) => setCfgValues(r.data?.values || {}))
      .catch(() => setCfgValues({}));
  }, [projectId]);
  const effectiveParams = useMemo(
    () => ({ ...(cfgValues || {}), ...(launcherParams || {}), ...overrides, render_stage: stepId }),
    [cfgValues, launcherParams, overrides, stepId],
  );
  const paramsKey = JSON.stringify(effectiveParams);
  const adjustedCount = useMemo(
    () => Object.keys({ ...(launcherParams || {}), ...overrides }).filter((k) => k !== 'render_stage' && cfgValues && String(effectiveParams[k] ?? '') !== String(cfgValues[k] ?? '') && !(effectiveParams[k] === '' && cfgValues[k] === undefined)).length,
    [launcherParams, overrides, cfgValues, effectiveParams],
  );
  const editParam = (key, value) => {
    setOverrides((o) => ({ ...o, [key]: value }));
    patchLauncherParams({ [key]: value });
  };
  const resetParam = (key) => {
    setOverrides((o) => { const n = { ...o }; delete n[key]; return n; });
    patchLauncherParams({ [key]: RESET_PARAM });
  };

  const lotItems = useMemo(() => (items || []).filter((i) => i.lot_id === lotId), [items, lotId]);
  const byStage = useMemo(() => {
    const m = {};
    WIZARD_STEPS.forEach((s) => {
      m[s.id] = lotItems.filter((i) => (i.params?.render_stage || 'full') === s.id)
        .sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
    });
    return m;
  }, [lotItems]);

  const stateOf = useCallback((step) => {
    const list = byStage[step.id] || [];
    if (justDone[`${lotId}:${step.id}`]) return 'done';
    if (step.refFlag ? list.some((i) => i[step.refFlag]) : list.some((i) => i.is_validated)) return 'done';
    const idx = WIZARD_STEPS.findIndex((s) => s.id === step.id);
    const prev = WIZARD_STEPS[idx - 1];
    if (prev && !justDone[`${lotId}:${prev.id}`] && !(byStage[prev.id] || []).some((i) => i[prev.refFlag])) return 'locked';
    if (list.some((i) => i.status === 'pending' || i.status === 'processing')) return 'pending';
    return 'ready';
  }, [byStage, justDone, lotId]);

  useEffect(() => {
    const first = WIZARD_STEPS.find((s) => stateOf(s) !== 'done') || WIZARD_STEPS[3];
    setStepId(first.id);
  }, [lotId]); // eslint-disable-line react-hooks/exhaustive-deps

  // V14.93/94 — scénario de l'étape courante : image d'appui + paramètres appliqués
  // (config projet + ajustements du lanceur + édition dans le wizard), debounce 450 ms.
  useEffect(() => {
    if (!lotId || !stepId || !cfgValues) return undefined;
    let cancelled = false;
    setScenarioLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await axios.post(`${API}/projects/${projectId}/generation/preview-prompt`, { lot_id: lotId, params: JSON.parse(paramsKey) }, opts);
        if (!cancelled) setScenario({ scenario: r.data?.scenario, variables: r.data?.variables || [] });
      } catch {
        if (!cancelled) setScenario(null);
      } finally {
        if (!cancelled) setScenarioLoading(false);
      }
    }, 450);
    return () => { cancelled = true; clearTimeout(t); };
  }, [projectId, lotId, stepId, justDone, cfgValues, paramsKey]);

  const step = WIZARD_STEPS.find((s) => s.id === stepId);
  const state = stateOf(step);
  const list = byStage[step.id] || [];
  const completed = list.filter((i) => i.status === 'completed' && i.image_url);
  const selectedId = choice[step.id] || completed.find((i) => (step.refFlag && i[step.refFlag]) || i.is_validated)?.job_id || completed.find((i) => i.review_status !== 'rejected')?.job_id || completed[0]?.job_id || '';
  const selected = completed.find((i) => i.job_id === selectedId);
  const pendingCount = list.filter((i) => i.status === 'pending' || i.status === 'processing').length;

  const launch = async (count = 1) => {
    setBusy(true);
    try {
      // V14.94 — mêmes paramètres que ceux affichés dans le scénario (lanceur + édition wizard).
      const params = { ...effectiveParams, render_stage: step.id };
      for (let k = 0; k < count; k += 1) {
        await axios.post(`${API}/projects/${projectId}/generation/create-batch`, { lot_ids: [lotId], params }, opts);
      }
      toast.success(`${count} rendu${count > 1 ? 's' : ''} « ${step.title} » lancé${count > 1 ? 's' : ''}`, {
        description: adjustedCount ? `${adjustedCount} paramètre${adjustedCount > 1 ? 's' : ''} ajusté${adjustedCount > 1 ? 's' : ''} appliqué${adjustedCount > 1 ? 's' : ''} · suivi dans la galerie.` : 'Le suivi se fait dans la galerie ; revenez ici pour valider.',
      });
      onChanged?.();
    } catch (e) {
      toast.error('Lancement impossible', { description: e?.response?.data?.detail || e?.message });
    } finally {
      setBusy(false);
    }
  };

  const validate = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      let anchored = false;
      if (selected.is_validated && (!step.refFlag || selected[step.refFlag])) {
        toast.info('Ce rendu est déjà validé et ancré.');
        anchored = true;
      } else if (selected.is_validated && step.refFlag) {
        await axios.post(`${API}/projects/${projectId}/generations/${selected.job_id}/stage-reference`, {}, opts);
        toast.success('Rendu ancré comme référence de l\'étape.');
        anchored = true;
      } else {
        const r = await axios.post(`${API}/projects/${projectId}/generations/${selected.job_id}/validate`, {}, opts);
        anchored = !!r.data?.auto_anchored || !step.refFlag;
        toast.success(r.data?.auto_anchored ? 'Rendu validé et ancré comme référence — étape suivante débloquée.' : 'Rendu validé.');
      }
      const idx = WIZARD_STEPS.findIndex((s) => s.id === step.id);
      const next = WIZARD_STEPS[idx + 1];
      if (anchored) setJustDone((d) => ({ ...d, [`${lotId}:${step.id}`]: true }));
      emitRefsChanged({ lotId, stage: step.id, anchored });
      onChanged?.();
      if (next && anchored) {
        setStepId(next.id);
        toast(`Étape ${next.n} · ${next.title}`, { description: 'Voici les paramètres qui seront appliqués — ajustez-les dans le lanceur puis lancez le rendu.' });
      }
    } catch (e) {
      toast.error('Validation impossible', { description: e?.response?.data?.detail || e?.message });
    } finally {
      setBusy(false);
    }
  };

  const shareStory = async () => {
    try {
      const r = await axios.post(`${API}/projects/${projectId}/lots/${lotId}/story-token`, {}, opts);
      const url = `${window.location.origin}${r.data.public_url}`;
      await navigator.clipboard?.writeText(url).catch(() => {});
      toast.success('Lien « histoire » copié', { description: `${r.data.stages_available.length} étape(s) · ${url}` });
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      toast.error('Partage impossible', { description: e?.response?.data?.detail || e?.message });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" style={{ background: 'var(--c-surface-0)', border: '1px solid var(--c-border)', color: 'var(--c-text)' }} data-testid="stage-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Wand2 className="w-4 h-4" style={{ color: 'var(--c-gold)' }} /> Parcours guidé — 4 étapes
            <span className="text-[11px] font-normal" style={{ color: 'var(--c-text-faint)' }}>valider un rendu l'ancre automatiquement pour l'étape suivante</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-3 flex-wrap">
          <span className="hud-eyebrow">Lot</span>
          <Select value={lotId} onValueChange={(v) => { setLotId(v); setChoice({}); }}>
            <SelectTrigger className="h-8 w-56 text-[12px] bg-white/[0.04] border-white/10 text-white" data-testid="wizard-lot">
              <SelectValue placeholder="Choisir un lot" />
            </SelectTrigger>
            <SelectContent>{lotOptions.map((o) => <SelectItem key={o.id} value={o.id}>Lot {o.label}</SelectItem>)}</SelectContent>
          </Select>
          <button type="button" onClick={shareStory} disabled={!lotItems.some((i) => i.status === 'completed' && i.params?.render_stage)}
                  className="ml-auto inline-flex items-center gap-1.5 text-[11.5px] px-2.5 py-1.5 rounded-lg hover:opacity-80 disabled:opacity-40"
                  style={{ color: 'var(--c-gold)', border: '1px solid rgba(217,160,91,0.4)' }} data-testid="wizard-share-story">
            <Share2 className="w-3.5 h-3.5" /> Partager l'histoire au client
          </button>
        </div>

        <div className="grid grid-cols-[220px_1fr] gap-4 mt-3">
          <div className="flex flex-col gap-1">
            {WIZARD_STEPS.map((s) => <StepHeader key={s.id} step={s} state={stateOf(s)} active={s.id === stepId} onClick={() => setStepId(s.id)} />)}
          </div>

          <div className="rounded-xl p-4 space-y-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--c-border)' }} data-testid="wizard-panel">
            <div>
              <div className="text-[13px] font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Étape {step.n} · {step.title}</div>
              <p className="text-[11.5px] mt-0.5" style={{ color: 'var(--c-text-2)' }}>{step.desc}</p>
            </div>

            {state === 'locked' && (
              <p className="text-[11.5px] px-3 py-2 rounded-lg" style={{ background: 'rgba(250,204,21,0.08)', color: '#FACC15', border: '1px solid rgba(250,204,21,0.3)' }} data-testid="wizard-locked">
                Validez d'abord l'étape précédente : son rendu ancré sert d'image d'appui à celle-ci.
              </p>
            )}

            {/* V14.93 — Scénario de l'étape : image d'appui + paramètres appliqués */}
            {scenarioLoading && !scenario && (
              <div className="flex items-center gap-1.5 text-[10.5px]" style={{ color: 'var(--c-text-faint)' }} data-testid="wizard-scenario-loading">
                <Loader2 className="w-3 h-3 animate-spin" /> Chargement du scénario de l'étape…
              </div>
            )}
            {scenario?.scenario && (
              <StageScenarioCard compact editable scenario={scenario.scenario} variables={scenario.variables}
                                 onChange={editParam} onReset={resetParam}
                                 onEdit={onOpenLauncher ? () => onOpenLauncher(stepId, lotId) : undefined} />
            )}

            <div className="grid grid-cols-[1fr_220px] gap-3">
              <div className="aspect-[4/3] rounded-lg overflow-hidden flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.35)', border: '1px solid var(--c-border)' }}>
                {selected ? <img src={fullImg(selected.image_url)} alt={step.title} className="w-full h-full object-contain" data-testid="wizard-selected-img" />
                  : <span className="text-[11px] px-3 text-center" style={{ color: 'var(--c-text-faint)' }}>{pendingCount ? `${pendingCount} rendu(s) en cours…` : 'Aucun rendu pour cette étape'}</span>}
              </div>
              <div className="flex flex-col gap-2">
                {completed.length > 0 && (
                  <Select value={selectedId} onValueChange={(v) => setChoice((c) => ({ ...c, [step.id]: v }))}>
                    <SelectTrigger className="h-8 text-[11px] bg-white/[0.04] border-white/10 text-white" data-testid="wizard-pick">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {completed.map((i) => (
                        <SelectItem key={i.job_id} value={i.job_id}>
                          {(step.refFlag && i[step.refFlag]) ? '⚓ ' : i.is_validated ? '✓ ' : i.review_status === 'rejected' ? '✗ ' : ''}{new Date(i.completed_at || i.created_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}{typeof i.fidelity_score === 'number' ? ` · plan ${i.fidelity_score}%` : ''}{i.review_rating ? ` · ★${i.review_rating}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {selected && (
                  <div className="text-[10.5px] space-y-0.5" style={{ color: 'var(--c-text-2)' }}>
                    {typeof selected.fidelity_score === 'number' && <div>Fidélité : <strong style={{ color: selected.fidelity_score >= 80 ? '#4ADE80' : selected.fidelity_score >= 60 ? '#FACC15' : '#F87171' }}>{selected.fidelity_score}%</strong></div>}
                    {typeof selected.utility_score === 'number' && <div>QA IA : {selected.utility_score}/100</div>}
                    {(step.refFlag && selected[step.refFlag]) && <div className="inline-flex items-center gap-1" style={{ color: '#F9A8D4' }}><Anchor className="w-3 h-3" /> Référence de l'étape</div>}
                  </div>
                )}
                {selected && (
                  <RenderReviewPanel
                    compact
                    item={selected}
                    projectId={projectId}
                    onChanged={() => onChanged?.()}
                    onRelaunched={() => onChanged?.()}
                  />
                )}
                <div className="mt-auto flex flex-col gap-1.5">
                  {step.id === 'whitebox' ? (
                    <DeterministicWhiteboxButton
                      compact
                      projectId={projectId}
                      lotId={lotId}
                      vueAngle={effectiveParams.vue_angle || 'Aérienne isométrique 45°'}
                      disabled={busy}
                      testId="wizard-whitebox-deterministic"
                      onDone={() => {
                        setJustDone((d) => ({ ...d, [`${lotId}:whitebox`]: true }));
                        onChanged?.();
                        setStepId('textured');
                        toast('Étape 2 · Texturer', { description: 'La maquette déterministe sert d\'image d\'appui — lancez le rendu texturé.' });
                      }}
                    />
                  ) : (
                  <button type="button" onClick={() => launch(1)} disabled={busy || state === 'locked'} data-testid="wizard-launch"
                          className="inline-flex items-center justify-center gap-1.5 text-[11.5px] px-3 py-2 rounded-lg font-semibold disabled:opacity-40"
                          style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--c-text)', border: '1px solid var(--c-border)' }}>
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Lancer {completed.length ? 'un autre rendu' : 'le rendu'}
                  </button>
                  )}
                  {adjustedCount > 0 && (
                    <span className="text-[9.5px] text-center" style={{ color: 'var(--c-gold)' }} data-testid="wizard-adjusted-hint">
                      avec {adjustedCount} paramètre{adjustedCount > 1 ? 's' : ''} ajusté{adjustedCount > 1 ? 's' : ''}
                    </span>
                  )}
                  {step.id !== 'whitebox' && (
                  <button type="button" onClick={() => launch(3)} disabled={busy || state === 'locked'} data-testid="wizard-launch-3"
                          className="text-[10.5px] underline hover:opacity-80 disabled:opacity-40" style={{ color: 'var(--c-text-muted)' }}>
                    Lancer 3 variantes
                  </button>
                  )}
                  <button type="button" onClick={validate} disabled={busy || !selected || selected.review_status === 'rejected'} data-testid="wizard-validate"
                          className="inline-flex items-center justify-center gap-1.5 text-[11.5px] px-3 py-2 rounded-lg font-semibold disabled:opacity-40"
                          style={{ background: 'var(--c-gold)', color: '#0F1115' }}>
                    <CheckCircle2 className="w-3.5 h-3.5" /> {step.refFlag ? 'Valider & ancrer' : 'Valider le rendu final'} {step.n < 4 && <ChevronRight className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StageWizard;
